import { Injectable, inject, signal } from '@angular/core';
import { NzConfigService } from 'ng-zorro-antd/core/config';
import { generate } from '@ant-design/colors';

/**
 * 页面主题。九个主题各是一份完整的 ng-zorro 样式表（八个预置 + 一个自定义）（**翠绿与绛紫共用同一份**，见
 * {@link APP_THEMES} 上头第四段）。
 */
export type AppTheme =
  | 'default'
  | 'purple'
  | 'light'
  | 'dark'
  | 'default-compact'
  | 'purple-compact'
  | 'compact'
  | 'dark-compact'
  | 'custom';

/**
 * 上次选的主题存在这里。键名是 snake_case，与别处的 UI 偏好键（`main.sidebarCollapsed`
 * 那种点号分区的）风格不同 —— 这个名字是随这份主题实现一起从 webapp-matrix 搬过来的，
 * 那边已经存在浏览器里了，改名只会让老用户的选择白丢一次。**`index.html` 那段首帧脚本读的
 * 也是这个键**，改一处要改两处。
 *
 * 只按**浏览器**记一份，不区分账号：主题是「这台机器的这块屏多大」的函数，不是账号属性
 * （自动挑默认值那条判据见 {@link COMPACT_MAX_VIEWPORT_H}）。
 *
 * 上一版（只有明暗开关）用的键是 `dark_mode`，**不做迁移**：那个开关当时全工程无人注入，
 * 页面上根本没有入口，所以不存在「用户选过」这件事 —— 没有要保的选择。
 */
const THEME_KEY = 'app_theme';

/**
 * 「矮屏就上紧凑」那条线（**视口**高度，CSS 像素）：**只在没存过主题时**用来挑默认值，
 * 见 {@link readTheme}。
 *
 * **判的是视口，不是屏幕分辨率** —— 分辨率判不出「小不小」：macOS 上 `screen.height` 报的是
 * **缩放后的逻辑分辨率**，13" MacBook 默认「看起来像 1440×900」报 900，27" 4K 默认缩放报 1080，
 * 而那台 4K 开 200% 缩放还是报 1080 —— 屏幕越大反而可能报得越小。真正决定「挤不挤」的是
 * 浏览器窗口，也就是视口。宽度同理：窗口拉小一半，分辨率一点没变。
 *
 * **为什么看高、不看宽**：这是个表格密集的后台，笔记本的问题是竖向放不下几行，不是横向。
 *
 * **900 这条线取的是空当**：笔记本屏（1440×900 / 1536×864 / 1366×768）最大化后视口高约
 * 813 / 777 / 681，1080p 屏约 993 —— 813 与 993 之间是空的，落在里面就不会卡在边界上
 * （浏览器自身占掉的那 87px 会随书签栏开关浮动，线贴着 813 的话同一台机器会时中时不中；
 * 87px 是量出来的：headless Chrome 开 700 高的窗口视口高 613，开 1000 是 913）。
 * 于是：三档笔记本 → 紧凑，1080p 及以上 → 常规。
 *
 * **只在这一页加载时判一次**：拖窗口、插拔外接屏都不重判 —— 用户没点的情况下主题自己变很怪，
 * 而且每跨越一次都要重新解析一份 570KB 的样式表。用户手动点过之后一律听用户的。
 *
 * **与 `index.html` 那段首帧脚本里抄的一份成对，改一处要改两处** —— 那段脚本也得按同一个阈值
 * 决定首帧用哪份样式表，否则小屏用户每次刷新都会先按翠绿渲染一帧再跳成紧凑。
 */
const COMPACT_MAX_VIEWPORT_H = 900;

/**
 * 主题样式表那个 `<link>` 的 id。
 *
 * `index.html` 上有一份（给首帧用的，见那边的注释），这里按 id 找它；找不到就自己建一个 ——
 * 挂载用例里没有 `index.html`。
 */
const LINK_ID = 'app-theme-css';

/**
 * 换主题那一帧挂在 `<html>` 上的类，用来关掉全部 `transition`，见 {@link ThemeService.apply} 第 0 条。
 *
 * **与 `styles.less` 顶上那条规则成对**（那边写的是选择器 `html.theme-switching *{transition:none}`），
 * 改一处要改两处 —— 名字写错不会报错，只是那一刻的过渡关不掉（症状回到「切主题闪一下」）。
 */
const SWITCHING_CLASS = 'theme-switching';

/** 一个主题的全部信息。对话框与 service **共用这一张表**，别处不再写第二份清单。 */
export interface AppThemeSpec {
  readonly id: AppTheme;
  /** 名字与副标题都是 i18n 键（中文原文，照仓库惯例）。 */
  readonly label: string;
  readonly desc: string;
  /** 自定义主题：`primary/tokens/ground` 只是默认占位，运行时按用户配置覆盖（见 apply 第 3 步）。 */
  readonly custom?: boolean;
  /** `themes/` 下的文件名。**与 `index.html` 里那段脚本的映射成对**，改一处要改两处。 */
  readonly file: string;
  /** 写进 `:root` 的 ng-zorro 主色，见 {@link ThemeService.apply} 第 4 条。 */
  readonly primary: string;
  /** 是否给 `body` 挂 `dark-theme`（`styles.less` 那一大块手写覆盖看的就是这个类）。 */
  readonly dark: boolean;
  /**
   * 挂在 `<html>` 上的 app 自绘 token。**五条主题非空**，分两族：
   *
   * 1. **跟主色走的那三个**（`--app-primary` / `-bg` / `-bg-strong`）：四条定制主题带
   *    （翠绿、绛紫与它们的紧凑版），换主色时一起重算。
   * 2. **只分深浅的那一族**（面 / 凹槽 / 悬停 / 按压、字与线、语义底）：两个玄黑带，
   *    见 {@link DARK_TOKENS}。**这一族不能靠回落值**：样式里那串
   *    `background: var(--app-surface, #fff)` 的兜底是**浅色**字面量，玄黑不挂值就是一块浅色
   *    压深底；做字 / 做线的那几支同理（兜底是近黑与浅灰）—— 「官方主题不带 token、
   *    反而逐像素一致」这条口径在**这些**上不成立，原因写全在 {@link DARK_TOKENS} 的注释里。
   *
   * 蔚蓝 / 蔚蓝紧凑仍然是空的，各处 `var()` 回落到原字面量，与加这个功能之前逐像素一致。
   * 理由写全在 {@link ThemeService.apply} 第 3 条。
   */
  readonly tokens: Readonly<Record<string, string>>;
  /** 卡片上第二个色块：这个主题的页面底色。 */
  readonly ground: string;
}

/**
 * 两个玄黑主题（官方暗黑那份与它的紧凑版）共同带的 app token：**只分深浅、不跟主色走**那一族。
 * 两条**引用同一个对象**，不是各写一份 —— 它们是同一个色、只差密度，抄两份迟早会漂。
 *
 * **为什么这两个主题必须有 token，而蔚蓝 / 翠绿可以没有**：样式表里每一处都写成
 * `background: var(--app-surface, #fff)` 这样**带原字面量兜底**的样子，而兜底值是**浅色**的。
 * 对前景色（`var(--app-primary, #1677ff)`）这没问题 —— 深底上那个蓝照样看得清；
 * 对**背景**就成了「一块浅色压深底」：玄黑下失效的不是那一处样式，是「官方主题不带 token」
 * 这条口径本身。所以凡是**做背景**的 token，两个玄黑都得给值。
 *
 * 同一条判据还有两族：**兜底在深底上不可用的**都得给值。做**字**的那几支兜底是近黑
 * （`var(--app-text, rgba(0,0,0,.85))`），做**线**的那几支兜底是浅灰（`#d9d9d9` 一类）——
 * 底色一旦跟着深了，压在上面的近黑字就成了深底深字、浅灰线成了一道亮边，
 * 这跟「浅底压深底」是同一个病：**改了底不改字，是把一处不显眼改成一处刺眼**。
 *
 * 上面这条判据落到各文件里，就是那一轮把全工程的浅底、近黑字、浅灰线与语义底一起并进来的
 * 扫描：`src/app/**` 下凡是**只有浅色那一半**的字面量都换成了 `var(--app-*, 原字面量)`，
 * 兜底值就是改动前那个数，所以浅色逐像素不变（同 {@link ThemeService.apply} 第 3 条）。
 * **故意留在字面量上的**是这几类：品牌亮色与语义**亮**字（`#ff4d4f` / `#ff7875` / `#52c41a` /
 * `#ff9f0a` 那一族 —— 深底上比浅底上更清楚，换深了反而错）、半透明的强调色（alpha ≤ .15，
 * 如拖拽落点框与点按水波，深浅两边都看得清）、**`#8c8c8c`**（它是中灰，在 `#1f1f1f`
 * 上是 5.5:1，本来就够读 —— 换成 `--app-text-muted` 反而把对比压到 4.3 以下）、
 * 以及阴影（`box-shadow` 里的黑，深浅两边都是「投在下面一层上」的意思，不跟主题走）。
 * **`src/styles.less` 那一大块不在这个扫描里**：它走的是自己那套字面量（`#000` / `#1f1f1f`
 * 与一串 `rgba(255,255,255,…)`），随 `body.dark-theme` 生效，与 token 各管各的 ——
 * 两支都指向同一套值（产物给 sider / header 的那一档 `#1f1f1f`），所以同时生效也不打架。
 *
 * 值全部取自 `ng-zorro-antd.dark.min.css` 自己，不是另配一套：
 *   `#1f1f1f`  产物给 `.ant-layout-sider` / `.ant-layout-header` / `.ant-modal-content` 的那一档
 *              （`styles.less` 顶上那句「底 `#000`、面 `#1f1f1f`」的「面」就是它）；
 *   `rgba(255,255,255,.04)`  产物给 `.ant-descriptions-item-label` / `.ant-table-footer` 那类
 *              凹槽用的 inset（产物里 9 处），也是它给**禁用态输入**的底色；
 *   `#303030`   产物给 `.ant-float-btn-body:hover` 一类悬停用的档；
 *   字与线那四支就是产物自己的三档：`rgba(255,255,255,.85)`（`@text-color`，`.ant-table` 与
 *              `.ant-page-header-heading-title` 都是它）、`.45`（`@text-color-secondary`）、
 *              `.12`（`@border-color-split`）、`.24`（同 `styles.less` 里 `--tree-border-hover`
 *              那一档 —— 两个值本来就是一处，写成一个数好对）；
 *   `#111b26` / `#15395b`  蓝的深色两档：`#111b26` 是产物给 `.ant-table-row-selected>td` 与
 *              `.ant-select-item-option-selected` 的选中底（18 处），`#15395b` 是它给
 *              `.ant-tag-blue` 的边（那枚标签自己就是 `#111d2c` 底 / `#15395b` 边 / `#3c9ae8` 字）。
 *              与浅色下 `--app-primary-bg` = 主色 1 档、`-strong` = 更重的一档（见上面「翠绿」
 *              那条的注释）对位 —— 那条「与产物里选中底是同一个值」的意图在深色下同样成立。
 *              （`styles.less` 里项目树手写的 `#111d2c` 与它差一个色阶，是既有不齐，本次不动。）
 *   语义那几族（`#2a1215` / `#2b2111` / `#162312`）取产物 `.ant-alert-*` 的底，配它**同族的**
 *              字与边 —— 产物给了成套的三元组，照抄 `.ant-tag-red`（`#2a1215` 底 / `#58181c` 边 /
 *              `#e84749` 字）那一行就是，不必另挑：
 *              error `#2a1215` + 字 `#e84749`；warning `#2b2111` + 边 `#594214`；success `#162312`。
 *              **只补有站点用的那几支**：`--app-error-line`（`#58181c`）与 success 的字 / 边、
 *              warning 的字暂时一处都没有，就不往里放 —— 表里放空档比缺档更容易让人以为「有那么个值」。
 *
 * **`--app-primary` 不在这里**（它是前景强调色）：玄黑下仍回落各处的 `#1677ff` / `#1890ff`，
 * 深底上看得很清楚；动它会让玄黑不再满足 {@link ThemeService.apply} 第 3 条写下的
 * 「四份官方产物与加这个功能之前逐像素一致」那条不变量，那是另一件事。
 */
const DARK_TOKENS: Readonly<Record<string, string>> = {
  /* 面 / 凹槽 / 悬停 / 按压 */
  '--app-surface': '#1f1f1f',
  '--app-inset': 'rgba(255, 255, 255, 0.04)',
  '--app-hover': '#303030',
  '--app-press': '#303030',
  /* 字与线：浅色下这几支的兜底是近黑 / 浅灰，深底上正好要反过来 */
  '--app-text': 'rgba(255, 255, 255, 0.85)',
  '--app-text-muted': 'rgba(255, 255, 255, 0.45)',
  '--app-line': 'rgba(255, 255, 255, 0.12)',
  '--app-line-strong': 'rgba(255, 255, 255, 0.24)',
  /* 语义底，以及有站点用到的那两支字 / 边 */
  '--app-error-bg': '#2a1215',
  '--app-error-text': '#e84749',
  '--app-warning-bg': '#2b2111',
  '--app-warning-line': '#594214',
  '--app-success-bg': '#162312',
  '--app-info-bg': '#111b26',
  '--app-info-bg-strong': '#15395b',
  /* 主色那一族里**做背景**的两支（做前景的 --app-primary 见上） */
  '--app-primary-bg': '#111b26',
  '--app-primary-bg-strong': '#15395b',
};

/**
 * 自定义主题存的东西：主色 + 四个语义色。**只存这五个值**，主色的 10 阶色板与 app token
 * 都由它们运行时算出来（见 {@link customTokens}），不落盘 —— 那是派生值，存了反而会漂。
 */
export interface CustomThemeConfig {
  /** 主色（强调色），决定整套界面最显眼的那一抹 */
  primary: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

/** 自定义主题在 localStorage 的键。照 `app_theme` / `app_theme_custom` 一套命名。 */
const CUSTOM_KEY = 'app_theme_custom';

/** 没存过自定义配置时的缺省值：主色用「翠绿」那套绿，语义色用 ng-zorro 默认那四个。 */
const DEFAULT_CUSTOM: CustomThemeConfig = {
  primary: '#09a679',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1890ff',
};

/**
 * 自定义主题注入的**语义色** CSS 变量名。这些是 `--ant-*` 不是 `--app-*`，
 * 不在 {@link APP_TOKEN_NAMES} 里（那张表只收集 app token）—— 于是换主题时得**单独**摘，
 * 否则从自定义切回预置主题时它们会留在 `<html>` 上，覆盖掉预置色的成功/警告/错误/信息。
 */
const CUSTOM_SEMANTIC_VARS = [
  '--ant-success-color',
  '--ant-warning-color',
  '--ant-error-color',
  '--ant-info-color',
];

/** 自定义配置 → app 自绘 token（主色那三个）。`--app-primary-bg` 两个浅色是主色的 1/2 档，
 *  与翠绿那条同一套算法（`generate()`），于是选中底等浅色面与 ng-zorro 推的色板同值。 */
function customTokens(cfg: CustomThemeConfig): Record<string, string> {
  const shades = generate(cfg.primary);
  return {
    '--app-primary': cfg.primary,
    '--app-primary-bg': shades[0],
    '--app-primary-bg-strong': shades[1],
  };
}

/** 自定义配置 → 语义色 CSS 变量（成功/警告/错误/信息，变量构建真的消费这四支）。 */
function customSemanticVars(cfg: CustomThemeConfig): Record<string, string> {
  return {
    '--ant-success-color': cfg.success,
    '--ant-warning-color': cfg.warning,
    '--ant-error-color': cfg.error,
    '--ant-info-color': cfg.info,
  };
}

/**
 * 九个主题。
 *
 * **为什么是「换文件」而不是「叠一层」**：这些产物都是完整、独立、写死颜色的样式表
 * （各含全部组件，选择器同名），叠在一起没有意义 —— 换主题就是换 `<link>` 的 href。
 * 只有「翠绿」与「绛紫」用的那份 `ng-zorro-antd.variable.min.css` 是 CSS 变量构建（554 处
 * `var(--ant-*)`），所以也只有它能在运行时换主色。
 *
 * **表里的顺序就是对话框里的顺序**：四张一行（`nzSpan="6"`），上排四个常规、下排四个紧凑，
 * 于是**同列同色**（翠绿 / 翠绿紧凑、绛紫 / 绛紫紧凑、蔚蓝 / 蔚蓝紧凑、玄黑 / 玄黑紧凑），
 * 一眼看得出是一家。**自定义是第 9 个、独占一格** —— 它没有「紧凑」另一半（紧凑 × 运行时可换色
 * 在本版本编不出产物，见 plan 的探索记录），所以只在表尾，不参与上面那套两两配对。
 * 列序（绿、紫、蓝、黑、自定义）是**定下来的**，不是按加进来的先后 —— 这个数组
 * 挪一下，对话框里卡片就跟着挪，别顺手「排个序」。
 *
 * **九个名字只有颜色**（翠绿 / 绛紫 / 蔚蓝 / 玄黑 / 翠绿紧凑 / 绛紫紧凑 / 蔚蓝紧凑 / 玄黑紧凑 /
 * 自定义），与卡片上那两个色块对得上；「主题」二字不写进名字里（这一栏本来就是主题对话框）。同列
 * 上下两条名字成对（颜色词相同、差「紧凑」二字），因为它们是**同一个色**、只差密度。
 * 名字就是 i18n 键（中文原文）—— 改名等于 66 份词典一起换键，别只动这里这九行字面量。
 *
 * **翠绿、绛紫与自定义共用一份 CSS**：三条都挂在变量构建上，颜色靠 `apply()` 第 4 条注入
 * ——「换主题 = 换文件」对这三条之间**不成立**（href 不动，只换 `:root` 里那组变量与 app token，
 * 连那份 570KB 都不用重新解析）。这也是「九个主题、七份文件」的由来：不要为了凑「一份一个」
 * 去编一份写死紫色的 CSS，那样反而丢掉运行时换色的能力。（自定义那条的注入值按用户存的配置，
 * 见 apply 第 3 步。）
 *
 * **三个紧凑的是自编产物**：ng-zorro 22.1.0 只发了四份（默认 / 暗黑 / 紧凑 / 变量），
 * 「紧凑 × 定制色」（绿、紫）与「紧凑 × 暗黑」这三份没有 —— 由 `themes/` 那个目录编出来，
 * 产物提交进仓库（`npm run build:themes`），缘由与坑见那边的 README。
 *
 * **没有阿里云主题**：ng-zorro 22.1.0 里那份产物是坏的 —— `ng-zorro-antd.aliyun.css` 去掉
 * `@keyframes` 后与官方默认那份逐字节相同（172 处 `#1890ff`、0 处 `#0070cc`），拿包里
 * `ng-zorro-antd.aliyun.less` 现场编一遍也还是默认蓝：那个入口先引阿里云变量、又引了带默认
 * 变量的 `style/entry.less`，less 里后定义者赢。照发出去就是「点了没反应」，故不做。
 */
export const APP_THEMES: readonly AppThemeSpec[] = [
  {
    // 翠绿：官方默认那份的**变量构建** + 一个绿色主色，其余全默认。
    // 变量构建只把颜色暴露成变量（32 个颜色变量 + 7 档主色），圆角与字号是写死的字面量 ——
    // 所以它是「官方默认那份换了配色」，不是另一套字体圆角。
    //
    // 那两个浅色是主色的 **1 档与 2 档**，用 `@ant-design/colors` 的 `generate()` 算出来的
    // —— 正是 ng-zorro 拿这个主色推色板时用的同一个函数（见 apply() 第 4 条）：
    //   node --input-type=module -e "import {generate} from '@ant-design/colors';console.log(generate('#09a679'))"
    //   → 1 档 #cfe6db · 2 档 #96d9bd · 6 档（即主色本身）#09a679
    // 于是 `--app-primary-bg` 与变量构建里 `.ant-table-row-selected` 那类选中底**是同一个值**：
    // 同一个主色下，app 自绘的浅色面与 ng-zorro 的浅色面总是一致。换主色时用上面那条命令重算。
    id: 'default',
    label: '翠绿',
    desc: '定制配色',
    file: 'ng-zorro-antd.variable.min.css',
    primary: '#09a679',
    dark: false,
    tokens: {
      '--app-primary': '#09a679',
      '--app-primary-bg': '#cfe6db',
      '--app-primary-bg-strong': '#96d9bd',
    },
    ground: '#ffffff',
  },
  {
    // 绛紫：与「翠绿」**同一份 CSS**（变量构建），只有注入的主色不同 —— 两条之间切换连
    // 样式表都不用重新解析，`apply()` 换掉 `:root` 里那组变量就完事（见 APP_THEMES 上头第四段）。
    //
    // 主色 #7546c9 是用户给的那个紫；名字取「绛紫」（传统色名，认识度最高）。两个浅色同翠绿那条：
    // 都是主色的 1 档与 2 档，用 `generate('#7546c9')` 算的 —— 1 档 #f8f0ff 很淡，那不是抄错，
    // 是 ant-design 的算法在这个色相上就这样，ng-zorro 注入的 `--ant-primary-1` 与它逐值相同。
    //
    // 排在第 2 列（`default` 之后），与「绛紫紧凑」上下成对 —— 见 APP_THEMES 上头第二段。
    id: 'purple',
    label: '绛紫',
    desc: '定制配色',
    file: 'ng-zorro-antd.variable.min.css',
    primary: '#7546c9',
    dark: false,
    tokens: {
      '--app-primary': '#7546c9',
      '--app-primary-bg': '#f8f0ff',
      '--app-primary-bg-strong': '#f5edfc',
    },
    ground: '#ffffff',
  },
  {
    // 官方默认那份（蔚蓝）。**与加这个功能之前的观感逐像素一致**，也是翠绿万一出问题时的退路。
    id: 'light',
    label: '蔚蓝',
    desc: '官方默认配色',
    file: 'ng-zorro-antd.min.css',
    primary: '#1890ff',
    dark: false,
    tokens: {},
    ground: '#ffffff',
  },
  {
    // 官方暗黑那份（玄黑）。主色取自那份产物自己的 `.ant-btn-primary`（#177ddc，不是默认的 #1890ff）。
    // 页面底色就是那份产物自己给 `body` 定的 `#000`（`styles.less` 深色那块的底也是它）——
    // 色块要预告的是用户真会看到的那块底。
    //
    // **带 token**：它是两条玄黑之一，背景类 token 必须有值，见 `DARK_TOKENS` 的注释。
    id: 'dark',
    label: '玄黑',
    desc: '官方暗黑配色',
    file: 'ng-zorro-antd.dark.min.css',
    primary: '#177ddc',
    dark: true,
    tokens: DARK_TOKENS,
    ground: '#000',
  },
  {
    // 翠绿紧凑：`themes/` 编出来的那份 —— 官方紧凑入口 + 绿色主色（ng-zorro 没发这一份）。
    // 与「翠绿」是**同一个绿**，所以 tokens 与它逐字相同，卡片上那两个色块也一模一样：
    // 两条的差别只在密度。副标题写「间距与字号更小」也是这个道理 ——「翠绿」这半截名字里已经有了。
    //
    // **主色写死在那份 CSS 里**（它没有 `var(--ant-*)`，下面 apply() 第 4 条对它无效），
    // 改主色要重编；`themes/build.mjs` 有一条断言盯着「这里的 tokens 与产物里的浅色面对不对得上」。
    id: 'default-compact',
    label: '翠绿紧凑',
    desc: '间距与字号更小',
    file: 'ng-zorro-antd.compact.green.min.css',
    primary: '#09a679',
    dark: false,
    tokens: {
      '--app-primary': '#09a679',
      '--app-primary-bg': '#cfe6db',
      '--app-primary-bg-strong': '#96d9bd',
    },
    ground: '#ffffff',
  },
  {
    // 绛紫紧凑：`themes/` 编出来的那份 —— 官方紧凑入口 + 紫主色（ng-zorro 没发这一份）。
    // 与「绛紫」是**同一个紫**，所以 tokens 与它逐字相同、两个色块也一模一样，差别只在密度；
    // 与「绛紫」不同的是这份**主色写死在产物里**（没有 `var(--ant-*)`），改主色要重编
    // —— `themes/build.mjs` 有一条断言盯着这里的 tokens 与产物里的浅色面对不对得上。
    id: 'purple-compact',
    label: '绛紫紧凑',
    desc: '间距与字号更小',
    file: 'ng-zorro-antd.compact.purple.min.css',
    primary: '#7546c9',
    dark: false,
    tokens: {
      '--app-primary': '#7546c9',
      '--app-primary-bg': '#f8f0ff',
      '--app-primary-bg-strong': '#f5edfc',
    },
    ground: '#ffffff',
  },
  {
    // 官方紧凑那份（蔚蓝紧凑）：底色与主色同蔚蓝（都是 #1890ff），差别只在密度
    // （基准字号 12px、按钮 28px 高）。
    id: 'compact',
    label: '蔚蓝紧凑',
    desc: '间距与字号更小',
    file: 'ng-zorro-antd.compact.min.css',
    primary: '#1890ff',
    dark: false,
    tokens: {},
    ground: '#ffffff',
  },
  {
    // 玄黑紧凑：`themes/` 编出来的那份 —— 官方暗黑入口 + 官方紧凑入口（ng-zorro 没发这一份）。
    // 颜色与官方暗黑那份逐值相同（主色同样是 #177ddc），所以除了密度，它与「玄黑」是同一条。
    id: 'dark-compact',
    label: '玄黑紧凑',
    desc: '间距与字号更小',
    file: 'ng-zorro-antd.compact.dark.min.css',
    primary: '#177ddc',
    dark: true,
    tokens: DARK_TOKENS,
    ground: '#000',
  },
  {
    // 自定义主题：**运行时按用户存的配置覆盖**，这里的三样只是「还没存过时」的默认占位。
    // 用官方变量构建（与翠绿/绛紫同一份）—— 只有它能运行时换主色（规则见 apply 第 4 条）。
    // 「紧凑 × 运行时可换色」在本 ng-zorro 版本编不出产物（见 plan 的探索记录），所以自定义
    // 暂时只有常规这一档，没有「自定义紧凑」。
    id: 'custom',
    label: '自定义',
    desc: '自定义配色',
    file: 'ng-zorro-antd.variable.min.css',
    primary: DEFAULT_CUSTOM.primary,
    dark: false,
    custom: true,
    tokens: customTokens(DEFAULT_CUSTOM),
    ground: '#ffffff',
  },
];

/**
 * 所有主题可能用到的 app token 名（定制主题那三个 + 两个玄黑那一族，见 {@link DARK_TOKENS}）。
 *
 * 从表里**推**出来而不是另写一份常量：加一个 token 只要动上面那张表，这里跟着走
 * —— 这一点现在更要紧：这张名单是「先全摘掉」那一步的输入，漏一个名字，
 * 从玄黑切到蔚蓝时它就会**留在 `<html>` 上**，浅色页面跟着一块深底走。
 * 切主题时先把它们全摘掉再挂当前主题的，见 {@link ThemeService.apply} 第 3 条。
 */
const APP_TOKEN_NAMES: readonly string[] = [
  ...new Set(APP_THEMES.flatMap((theme) => Object.keys(theme.tokens))),
];

/**
 * 读出这一页该用哪个主题。
 *
 * **存过有效值就一律听用户的**（含「小屏用户自己点了翠绿」这种）—— 那是显式选择，
 * 屏幕多小都不推翻。读出来的值要能对上 {@link APP_THEMES} 里的 id：这个键用户能看见、
 * 也能手改，还可能是上一版留下的值（`dark_mode` 那代），不能直接当 `AppTheme` 用 ——
 * 所以这里逐条比对 id，认不出的一律当作「没有选择」。
 *
 * **没有一个有效的用户选择时**（没存过，或存的是认不出的值 —— 两者都表示「这里没有选择」），
 * 按视口高度挑默认值：矮屏回「翠绿紧凑」（`default-compact`），否则回「翠绿」（`default`）。
 * 阈值与缘由见 {@link COMPACT_MAX_VIEWPORT_H}。
 *
 * 自动挑出来的那个值**不写进 localStorage**（这条读的函数本来就不写）：它是个「这一屏多大」的
 * 临时结论，写进去就等于把某次开窗的尺寸永久钉死了 —— 下次换到大屏还是紧凑，而用户从没点过
 * 任何东西。只有 {@link ThemeService.setTheme}（用户点的那一下）才落盘。
 *
 * 没有 try/catch：本仓库别处读写 `localStorage`（`account.service`、设备页的视图方式）也都没包，
 * 而这是个纯浏览器端应用（没有 SSR，`localStorage` 一定在）。
 */
function readTheme(): AppTheme {
  const saved = localStorage.getItem(THEME_KEY);
  if (APP_THEMES.some((theme) => theme.id === saved)) {
    return saved as AppTheme;
  }
  return window.innerHeight < COMPACT_MAX_VIEWPORT_H ? 'default-compact' : 'default';
}

/**
 * 页面主题的切换与记忆。
 *
 * **在启动时被构造一次**才有意义（`app.component` 里注入的那一行就是干这个的）：构造函数里
 * 会把存过的主题落到页面上，晚于首帧就会看到「先默认、再跳成存的那个」。
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** ng-zorro 的动态主题特性入口（`providedIn: 'root'`，不用 `provideNzConfig`）。 */
  private readonly config = inject(NzConfigService);

  /** 当前主题。初始值取上次选的那个 —— 构造函数里已经应用过了。 */
  readonly theme = signal<AppTheme>(readTheme());

  constructor() {
    this.apply(this.spec(this.theme()));
  }

  /**
   * 换主题。存的是**用户点的那一下**，不在信号上挂 effect 回写（与设备页的视图方式同一个理由：
   * 信号建的时候就要读一次 localStorage，用 effect 会把刚读出来的值又原样写回去一次）。
   *
   * 点的就是当前那个则直接返回：既不重写 localStorage，也不去动 `<link>`（换 href 会让浏览器
   * 重新解析那份 570KB 的样式表，白费）。
   */
  setTheme(id: AppTheme): void {
    if (id === this.theme()) {
      return;
    }
    this.theme.set(id);
    localStorage.setItem(THEME_KEY, id);
    this.apply(this.spec(id));
  }

  private spec(id: AppTheme): AppThemeSpec {
    return APP_THEMES.find((theme) => theme.id === id) ?? APP_THEMES[0];
  }

  /**
   * 读自定义主题配置。**存过就听用户的、缺的字段补默认值；认不出的就当没存过**（照
   * `readTheme` 那条口径）—— 这个对象用户能看见也能手改，不能直接当 {@link CustomThemeConfig} 用。
   */
  customConfig(): CustomThemeConfig {
    const fallback = { ...DEFAULT_CUSTOM };
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(CUSTOM_KEY);
    } catch {
      /* 读不到就当没存过，不影响页面（与 readStored 那条同样的兜底） */
    }
    if (!raw) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) {
        return fallback;
      }
      return { ...fallback, ...parsed };
    } catch {
      return fallback;
    }
  }

  /**
   * 存自定义主题配置，并在「当前正用自定义主题」时立即重挂 —— 那正是自定义页保存后要的效果：
   * 改完不用切走再切回来。
   */
  saveCustomConfig(cfg: CustomThemeConfig): void {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(cfg));
    if (this.theme() === 'custom') {
      this.apply(this.spec('custom'));
    }
  }

  /** 对话框卡片上自定义主题要显示的两个色块（primary / ground）—— 取用户当前配置，不是表的占位值。 */
  customPreview(): { primary: string; ground: string } {
    return { primary: this.customConfig().primary, ground: '#ffffff' };
  }

  /**
   * 只把自定义配色**临时**挂到页面上给实时预览：注入 app token + 语义色 + `NzConfig` 主色，
   * **不动** `<link>` / dark 类 / 主题 id / localStorage —— 于是改色时整页（含卡片 / 表格预览）
   * 跟着变，但这不是「选中自定义主题」，不落盘也不改当前主题名。
   *
   * 离开这页（`ngOnDestroy`）要调 {@link refresh} 把真正的主题挂回来。
   */
  previewCustom(cfg: CustomThemeConfig): void {
    for (const name of [...APP_TOKEN_NAMES, ...CUSTOM_SEMANTIC_VARS]) {
      document.documentElement.style.removeProperty(name);
    }
    for (const [name, value] of Object.entries(customTokens(cfg))) {
      document.documentElement.style.setProperty(name, value);
    }
    for (const [name, value] of Object.entries(customSemanticVars(cfg))) {
      document.documentElement.style.setProperty(name, value);
    }
    this.config.set('theme', { primaryColor: cfg.primary });
  }

  /** 把**当前选中的主题**重新挂一遍（预览完 / 页面离开时恢复到真实主题）。 */
  refresh(): void {
    this.apply(this.spec(this.theme()));
  }

  /**
   * 把主题落到页面上。五件事，各自的理由：
   *
   * 0. **先把过渡全关掉**（`<html>` 挂 {@link SWITCHING_CLASS}，规则在 `styles.less` 顶上）——
   *    主题产物里有 264 处 `transition`（`.ant-btn` 都是 `transition: all .3s`），而**换文件时
   *    浏览器会把这些属性从旧值动画到新值**：整页要爬 300ms，颜色还穿过一串灰调。这不是猜的，
   *    是 headless Chrome 逐帧量出来的（切主题时逐帧读 `getComputedStyle`）：
   *      翠绿 → 绛紫：按钮底色 rgb(9,166,121) → … → rgb(117,70,201)，中间路过 rgb(49,130,151)
   *      蔚蓝 → 蔚蓝紧凑：字号 14px → 13.92 → 13.78 → … → 12px（连密度都在爬）
   *    用户看到的「闪一下」就是这 300ms 的爬行。挂上这个类，下面四件事落在**同一帧**里，
   *    切换是干净的一下。（`transition` 而不是 `animation`：对话框关窗是 keyframes，不受影响。）
   *
   * 1. **换 `<link>` 的 href** —— 七份 CSS 是七份完整、互不叠加的样式表（见 {@link APP_THEMES}），
   *    换主题就是换文件。link 用「找不到就建一个」的写法，挂载用例里也能跑。
   *    **翠绿 ↔ 绛紫之间这一条是空转**（两条同一个文件）：href 写同一个值时浏览器不动它。
   *    （也量过：换 href 不会闪出「没有样式的一帧」—— 旧那份一直生效到新的接手，全程只有一份。）
   * 2. **`body.dark-theme`** —— `styles.less` 那一大块手写覆盖看的就是这个类。它只影响 app
   *    自绘的表面（看板、项目树、对话框…），而那些表面要等 Angular 渲染才有，所以这个类
   *    晚一点挂不闪 —— 深色不闪白靠的是第 1 条（那份 CSS 自己就给 `body` 上了深底）。
   * 3. **app 自绘的 token** —— 先按 {@link APP_TOKEN_NAMES} 全摘掉，再挂当前主题的。
   *    两个玄黑带 {@link DARK_TOKENS}（背景类那一族），四条定制主题带主色那三个，
   *    蔚蓝与蔚蓝紧凑 `tokens` 是空的。样式表里那一串是**带原字面量兜底**的
   *    （`var(--app-primary, #1677ff)`、`var(--app-surface, #fff)`），摘掉 token 就是各处原本的
   *    颜色 —— 于是蔚蓝 / 蔚蓝紧凑渲染出来与加这个功能之前**逐像素一致**，定制主题下 app 自绘的
   *    强调色（左栏悬停、项目树、看板、功能码徽标…）跟着主色走，而两个玄黑下 app 自绘的**底色**
   *    跟着深色走（不做这一步，玄黑里那些 `var(…, #fff)` 会落成一块块浅色压在深底上）。
   * 4. **`NzConfigService.set('theme', …)`** —— ng-zorro 的动态主题特性：它用 TinyColor 从
   *    一个主色推出 10 阶色板与 hover / active / outline / deprecated-* 等，拼成 `:root{--ant-*}`
   *    注入一个 `<style>`（mark 是模块级常量，反复 set 只改同一个节点，不堆积）。
   *    `:root` 的优先级高于变量构建里那个 `html{…}`，所以注入值赢。
   *    **只有变量构建消费这些变量**（另外六份写死颜色），所以这一条只在翠绿与绛紫上真的生效；
   *    在那六份上写进去是空转 —— 写它只为让 `:root` 始终描述当前主题，别停在上一个主色上。
   */
  private apply(spec: AppThemeSpec): void {
    const root = document.documentElement;
    root.classList.add(SWITCHING_CLASS);

    this.themeLink().href = `themes/${spec.file}`;

    document.body.classList.toggle('dark-theme', spec.dark);

    // app token **和**自定义的语义色一起先全摘掉，再挂当前主题的。语义色是 `--ant-*`
    // 不在 `APP_TOKEN_NAMES` 里，单独摘 —— 否则从自定义切回预置主题时四个语义色会残留，
    // 覆盖掉预置的成功/警告/错误/信息色。
    for (const name of [...APP_TOKEN_NAMES, ...CUSTOM_SEMANTIC_VARS]) {
      document.documentElement.style.removeProperty(name);
    }

    // 自定义主题：primary 与 token 都按用户存的配置运行时覆盖（表里那三样只是占位）。
    let primaryColor = spec.primary;
    let tokens = spec.tokens;
    let semantic: Record<string, string> = {};
    if (spec.custom) {
      const cfg = this.customConfig();
      primaryColor = cfg.primary;
      tokens = customTokens(cfg);
      semantic = customSemanticVars(cfg);
    }
    for (const [name, value] of Object.entries(tokens)) {
      document.documentElement.style.setProperty(name, value);
    }
    for (const [name, value] of Object.entries(semantic)) {
      document.documentElement.style.setProperty(name, value);
    }

    this.config.set('theme', { primaryColor });

    // 摘类的时机：**新值落了一帧之后**才摘。过渡启不启动，比的是「这一帧算出来的值」与「上一帧的
    // 值」—— 同一帧摘掉等于没关。两个 `requestAnimationFrame`：第一个在本帧的回调里排队，第二个
    // 落在再下一帧，那时带类的那一帧已经提交（值是新的、类还在，没人从旧值爬起来），才把类摘掉。
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove(SWITCHING_CLASS)),
    );
  }

  /** `index.html` 上那份主题 `<link>`；没有就建一个（挂载用例里没有 index.html）。 */
  private themeLink(): HTMLLinkElement {
    const found = document.getElementById(LINK_ID);
    if (found instanceof HTMLLinkElement) {
      return found;
    }
    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return link;
  }
}
